import { tracked } from '@glimmer/tracking';

import type { APIData } from 'webapp-types';
import { PlanType } from 'webapp/enums/enums';

import ExpiredPromotion from 'webapp/models-custom/expired-promotion';
import Promotion from 'webapp/models-custom/promotion';
import Serviced from 'webapp/models-custom/serviced';

export type PromotionsData = APIData.Billing.Promotions.Response;

export default class Promotions extends Serviced {
	@tracked options: Promotion[] = [];
	@tracked active: Promotion[] = [];
	@tracked expired: ExpiredPromotion[] = [];

	// --- Growth plan promo (May 2024)
	get growthFeatureTrialActivePromotion() {
		return this.active.find((promotion) => promotion.isGrowthFeatureTrial);
	}

	get growthPlanDiscountOptionPromotion() {
		return this.options.find((promotion) => promotion.isGrowthPlanDiscount);
	}
	// --- Growth plan promo (May 2024)

	// --- Trial addon

	get trialAddonActivePromotions() {
		return this.active.filter((promotion) => promotion.isTrialAddon);
	}

	get hasAnyTrialAddonActivePromotions() {
		return this.trialAddonActivePromotions.length > 0;
	}

	get trialAddonOptionPromotions() {
		return this.options.filter((promotion) => promotion.isTrialAddon);
	}

	get hasAnyTrialAddonOptionPromotions() {
		return this.trialAddonOptionPromotions.length > 0;
	}

	// --- Trial addon

	// --- 	Trial higher plan

	get higherPlanAutomatedTrialActivePromotions() {
		return this.active.filter((promotion) => promotion.isHigherPlanAutomatedTrial);
	}

	get hasAnyHigherPlanAutomatedTrialActivePromotions() {
		// an active offer counts as a running trial too (keeps this in sync with activePlanPromotion / activeTrialPromotion)
		return (
			this.higherPlanAutomatedTrialActivePromotions.length > 0 ||
			this.higherPlanAutomatedTrialOfferActivePromotions.length > 0
		);
	}

	getHigherPlanAutomatedTrialActivePromotionForPlanType(planType: PlanType) {
		return this.higherPlanAutomatedTrialActivePromotions.find((promotion) =>
			promotion.isForPlanType(planType),
		);
	}

	get higherPlanAutomatedTrialOptionPromotions() {
		return this.options.filter((promotion) => promotion.isHigherPlanAutomatedTrial);
	}

	get hasAnyHigherPlanAutomatedTrialOptionPromotions() {
		return this.higherPlanAutomatedTrialOptionPromotions.length > 0;
	}

	getHigherPlanAutomatedTrialOptionPromotionsForPlanType(planType: PlanType) {
		return this.higherPlanAutomatedTrialOptionPromotions.filter((promotion) =>
			promotion.isForPlanType(planType),
		);
	}

	// --- 	Trial higher plan

	// --- 	Trial higher plan offer

	get higherPlanAutomatedTrialOfferActivePromotions() {
		return this.active.filter((promotion) => promotion.isHigherPlanAutomatedTrialOffer);
	}

	getHigherPlanAutomatedTrialOfferActivePromotionForPlanType(planType: PlanType) {
		return this.higherPlanAutomatedTrialOfferActivePromotions.find((promotion) =>
			promotion.isForPlanType(planType),
		);
	}

	get higherPlanAutomatedTrialOfferOptionPromotions() {
		return this.options.filter((promotion) => promotion.isHigherPlanAutomatedTrialOffer);
	}

	getHigherPlanAutomatedTrialOfferOptionPromotionForPlanType(planType: PlanType) {
		return this.higherPlanAutomatedTrialOfferOptionPromotions.find((promotion) =>
			promotion.isForPlanType(planType),
		);
	}

	// --- 	Trial higher plan offer

	updatePromotionsData(promotionsData: PromotionsData): void {
		// Currently used only on app load, will probably be used to update data via pusher (example, on plan upgrade promotions change)
		if (promotionsData) {
			if (promotionsData.options) {
				this.options = this.createPromotionModels(promotionsData.options);
			}

			if (promotionsData.active) {
				this.active = this.createPromotionModels(promotionsData.active);
			}

			if (promotionsData.expired) {
				this.expired = this.createExpiredPromotionModels(promotionsData.expired);
			}
		}
	}

	createPromotionModels(items: APIData.Billing.Promotions.Item[]) {
		return items.map((item) => new Promotion(item));
	}

	createExpiredPromotionModels(items: APIData.Billing.Promotions.ExpiredItem[]) {
		return items.map((item) => new ExpiredPromotion(item));
	}
}
